<template>
    <VJoyDialog
        ref="dialogRef"
        class="mui-c-dialog-fullscreen mui-background"
        v-bind="$attrs"
        size="large"
        :data-mui-theme="props.theme"
        @dialog:after-open="afterOpenDialog"
    >
        <template #dialog-body>
            <div ref="bodyRef" class="mui-c-dialog-fullscreen__body">
                <slot />
            </div>
        </template>
        <template #dialog-cancel>
            <VJoyButton variant="ghost" size="small" @click="closeDialog">Close</VJoyButton>
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {useTemplateRef} from 'vue';
    import {VJoyDialog, VJoyButton} from '@maltjoy/core-vue';
    import type {MuiTheme} from '../types/theme';

    defineOptions({
        inheritAttrs: false,
    });

    const props = defineProps<{
        theme?: MuiTheme;
    }>();

    const dialogRef = useTemplateRef<InstanceType<typeof VJoyDialog>>('dialogRef');

    defineExpose({
        show: (...args: Parameters<NonNullable<InstanceType<typeof VJoyDialog>['show']>>) => {
            dialogRef.value?.show(...args);
        },
        hide: (...args: Parameters<NonNullable<InstanceType<typeof VJoyDialog>['hide']>>) => dialogRef.value?.hide(...args),
    });

    function closeDialog() {
        dialogRef.value?.hide();
    }
    const bodyRef = useTemplateRef<HTMLDivElement>('bodyRef');

    function afterOpenDialog() {
        bodyRef.value?.parentElement?.focus();
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .mui-c-dialog-fullscreen {
        &.joy-dialog {
            &,
            .joy-dialog--wrapper,
            .joy-dialog__inner {
                transition: none;

                --dialog-width: 100vw;
                --dialog-height: 100vh;

                height: 100vh;
                width: 100vw;
                max-height: 100vh;
                max-width: 100vw;
                padding: 0;
                background-color: var(--mui-color-background);

                .joy-dialog--body {
                    height: calc(100vh - 100px);
                    max-height: calc(100vh - 100px);
                    margin: var(--joy-core-spacing-6);

                    @include mq.screen_sm(min) {
                        margin: var(--joy-core-spacing-12);
                    }

                    @include mq.screen_md(min) {
                        margin: var(--joy-core-spacing-12);
                    }

                    overflow: hidden;
                }

                .joy-dialog--footer {
                    justify-content: center;
                    top: calc(var(--joy-core-spacing-6) * -1);
                }
            }

            .joy-dialog__overlay {
                background: none;
            }
        }

        &__body {
            height: 100%;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            gap: var(--joy-core-spacing-4);
            flex-direction: column;

            & > img {
                flex: 1 1 0;
                min-width: 0;
                min-height: 0;
                max-width: 100%;
                max-height: 100%;
                width: auto;
                height: auto;
                object-fit: contain;
                border-radius: var(--joy-core-radius-3);
            }

            @include mq.screen_sm(min) {
                flex-direction: row;
            }
        }
    }
</style>
